import { Item } from '../../services/graphql.service';
import { ReservationServiceCategory } from './reservation-service-category.model';
import { CategoriesWithQuantity, CategoriesWithRules } from './reservation-types';

import * as lodash from 'lodash';

export interface IReservationServiceItem {
  serviceId?: string;
  serviceName?: string;
  serviceDescription?: string;
  servicePrice?: number;
  isPrimary?: boolean;

  // Quantity
  needQuantityInput?: boolean;
  serviceQuantity?: number;

  linkedService: Item;

  //
  serviceCategoryId?: string;
  serviceCategoryName?: string;

  //
  isSelected?: false;
}

export class ReservationServiceItem {

  // #region -> Property <-
  // ----------

  serviceId: string;
  serviceName: string;
  serviceDescription: string;
  servicePrice = 0;
  isPrimary = false;

  // Quantity
  needQuantityInput = false;
  serviceQuantity = 1; // Most services will happen exactly one time

  linkedService: Item;

  //
  serviceCategoryId: string;
  serviceCategoryName: string;

  //
  isSelected: false;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(newItem: IReservationServiceItem) {
    //
    this.serviceId = newItem.serviceId || undefined;
    this.serviceName = newItem.serviceName || undefined;
    this.serviceDescription = newItem.serviceDescription || undefined;
    this.servicePrice = newItem.servicePrice || 0;
    this.isPrimary = newItem.isPrimary || false;
    this.needQuantityInput = newItem.needQuantityInput || false;
    this.serviceQuantity = newItem.serviceQuantity || 1;
    this.linkedService = newItem.linkedService || undefined;
    this.serviceCategoryId = newItem.serviceCategoryId || undefined;
    this.serviceCategoryName = newItem.serviceCategoryName || undefined;
    this.isSelected = newItem.isSelected || false;

    //
    this.needQuantityInput = this._CheckIfNeedQuantityInput();
    this._updateServiceInfo();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Immutable <-
  // ----------

  json(): IReservationServiceItem {
    //
    return lodash.cloneDeep({
      serviceId: this.serviceId,
      serviceName: this.serviceName,
      serviceDescription : this.serviceDescription,
      servicePrice: this.servicePrice,
      isPrimary: this.isPrimary,
      needQuantityInput: this.needQuantityInput,
      serviceQuantity: this.serviceQuantity,
      linkedService: this.linkedService,
      serviceCategoryId: this.serviceCategoryId,
      serviceCategoryName: this.serviceCategoryName,
      isSelected: this.isSelected,
    });
  }

  clone(): ReservationServiceItem {
    //
    return new ReservationServiceItem(this.json());
  }

  update(updatedData: Partial<IReservationServiceItem>): ReservationServiceItem {
    //
    const newData: IReservationServiceItem = {...this.json(), ...updatedData};

    // Return a new reference
    return new ReservationServiceItem(newData);
  }

  // ----------
  // #endregion

  /////////////////////////////


  // #region -> Method - Get <-
  // ----------

  canBeSelectable(serviceCategoryIdsList: string[]): boolean {
    // Rule 1 - If already have "FSG" service, cannot select "Bath" and "Grooming" services.
    if (serviceCategoryIdsList.includes(CategoriesWithRules.FullServiceGrooming) &&
      (this.serviceCategoryId === CategoriesWithRules.Bath || this.serviceCategoryId === CategoriesWithRules.Grooming)) {
      // Not selectable
      return false;
    }

    return true;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Set <-
  // ----------

  updateCategoryInfo(category: ReservationServiceCategory): ReservationServiceItem {
    //
    if (category) {
      this.serviceCategoryId = category.categoryId;
      this.serviceCategoryName = category.categoryName;

      // Check if need quantity
      this.needQuantityInput = this._CheckIfNeedQuantityInput();
    }

    //
    return this.clone();
  }

  // ----------
  // #endregion

  /////////////////////////////


  // #region -> Private Method <-
  // ----------

  private _CheckIfNeedQuantityInput(): boolean {
    //
    return CategoriesWithQuantity.includes(this.serviceCategoryId as CategoriesWithRules);
  }

  private _updateServiceInfo(): void {
    //
    if (this.linkedService) {
      this.serviceId = this.linkedService.id;
      this.serviceName = this.linkedService.displayName;
      this.serviceDescription = this.linkedService.description;
      this.isPrimary = !lodash.isNil(this.linkedService.isPrimary) ? this.linkedService.isPrimary : false;
      this.servicePrice = this.linkedService.price;
    }
  }

  // ----------
  // #endregion

  /////////////////////////////

}
